import { useEffect, useState } from "react"
import { Box, Divider, Typography, useMediaQuery } from "@mui/material"
import { MainPageContentContainer } from "components/MainPageContentContainer"
import { OreveButton } from "components/button/OreveButton.tsx"
import { UseRfidContext } from "layouts/rfid/context/provider.tsx"
import { ArrowBack } from "@mui/icons-material"
import { ChargingRulesGlobalComponent } from "layouts/charging-rules/components/ChargingRulesGlobalComponent.tsx"
import { BackendSchema } from "layouts/charging-rules/interface/Rules.ts"
import { useChargingRulesContext } from "layouts/charging-rules/context/useChargingRulesContext.ts"
import { Loader } from "components/Loader.tsx"
import { OreveFooter } from "components/OreveFooter.tsx"
import { useMmsService } from "services/mms/context/MmsServiceContext.tsx"
import { BackendFeedbackPopup } from "components/popup/BackendFeedbackPopup.tsx"
import CreditCardIcon from "@mui/icons-material/CreditCard"
import { noRulesApplied } from "layouts/rfid/utils/const.ts"
import { transformRulesToBackendSchema } from "layouts/charging-rules/utils/utils.ts"
import { syncSchemaAfterRfidUpdate } from "layouts/rfid/utils/functions.ts"

export const EditRuleRfid = () => {
    const { rfidRuleService } = useMmsService()
    const { useUpdateRfidCardRule } = rfidRuleService
    const [validateNewRule, setValidateNewRule] =
        useState<BackendSchema>(noRulesApplied)
    const [showSuccessPopup, setShowSuccessPopup] = useState(false)
    const [done, setDone] = useState(false)
    const { setViewMode, currentCardSelected, setCurrentCardSelected } =
        UseRfidContext()
    const {
        rules,
        dispatch,
        schema,
        setSchema,
        ruleSchemaCompany,
        getRfidCardRuleLoading,
        rfidCardRuleId,
        isRuleChanged,
        setIsRuleChanged,
        errorDuration,
        setErrorDuration,
        errorEnergy,
        setErrorEnergy,
        allHoursSelected,
        allDaysSelected,
        setRfidCardRuleId,
    } = useChargingRulesContext()

    const {
        sendRequest: updateRfidCardRule,
        success: updateRfidCardRuleSuccess,
        isLoading: updateRfidCardRuleLoading,
        data: updateRfidCardRuleData,
    } = useUpdateRfidCardRule(rfidCardRuleId ?? "", validateNewRule)

    const isTablet = useMediaQuery("(max-width:900px)")

    useEffect(() => {
        if (!updateRfidCardRuleLoading && updateRfidCardRuleSuccess === true) {
            setShowSuccessPopup(true)
        }
    }, [updateRfidCardRuleLoading, updateRfidCardRuleSuccess])

    useEffect(() => {
        if (done) {
            updateRfidCardRule(validateNewRule)
            setDone(false)
        }
    }, [done])

    const validation = async () => {
        const result = transformRulesToBackendSchema(rules)
        setValidateNewRule(result)
        setDone(true)
        setIsRuleChanged(true)
    }

    const goBackToList = () => {
        setShowSuccessPopup(false)
        setErrorEnergy(false)
        setErrorDuration(false)
        setIsRuleChanged(true)
        setCurrentCardSelected(null)
        setRfidCardRuleId(null)
        setDone(false)
        setViewMode("LIST")
    }

    const mainContainerStyles = {
        margin: "0px",
        width: "calc(100vw - (1.5rem + 1rem))",
        height: "calc(100vh - (1.5rem + 132px + 2.5rem ))",
        overflowY: "auto",
    } as const

    return getRfidCardRuleLoading ? (
        <MainPageContentContainer sx={mainContainerStyles}>
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100%",
                    width: "100%",
                }}
            >
                <Loader />
            </Box>
        </MainPageContentContainer>
    ) : (
        <>
            <MainPageContentContainer sx={mainContainerStyles}>
                <BackendFeedbackPopup
                    open={showSuccessPopup || updateRfidCardRuleLoading}
                    onClose={() => {
                        setShowSuccessPopup(false)
                        setCurrentCardSelected(null)
                        goBackToList()
                    }}
                    onContinue={() => {
                        setShowSuccessPopup(false)
                        setErrorEnergy(false)
                        setErrorDuration(false)
                        syncSchemaAfterRfidUpdate(
                            updateRfidCardRuleData as BackendSchema,
                            schema,
                            setSchema,
                            ruleSchemaCompany,
                            dispatch
                        )
                        setIsRuleChanged(true)
                    }}
                    success={updateRfidCardRuleSuccess ?? false}
                    successTitle={`Les règles s’appliqueront à la carte n°${currentCardSelected?.id}`}
                    successContent={
                        "Vous pouvez consulter ou modifier ces règles à tout moment."
                    }
                    errorContent={
                        "Une erreur est survenue lors de la modification des règles."
                    }
                    buttonIcon={CreditCardIcon}
                    buttonText={"Revenir à la liste des cartes"}
                    isLoading={updateRfidCardRuleLoading && !showSuccessPopup}
                />

                <OreveButton
                    sx={{
                        position: "absolute",
                        marginTop: "10px",
                        textDecoration: "none",
                        "&:hover, &:focus": {
                            backgroundColor: "transparent",
                            textDecoration: "none",
                        },
                    }}
                    variant="text"
                    value="Retour à la liste de cartes"
                    onClick={goBackToList}
                    icon={ArrowBack}
                />
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            marginTop: "3.688rem",
                            marginLeft: "2.188rem",
                        }}
                    >
                        <Typography variant="headlineLarge">
                            Personnalisation des règles
                        </Typography>

                        <Typography variant="body2" color="textSecondary">
                            Sélectionnez les règles de recharge spécifiques qui
                            s'appliqueront à la carte RFID n°
                            {currentCardSelected?.rfidNumber}.
                        </Typography>

                        {rules.map((rule, index) => (
                            <Box
                                key={`${index}`}
                                sx={{
                                    mt: "4rem",
                                    width: "100%",
                                    height: "100%",
                                }}
                            >
                                <ChargingRulesGlobalComponent
                                    key={rule.type}
                                    rule={rule}
                                    isRfid={true}
                                />
                                {isTablet && index < rules.length - 1 && (
                                    <Divider key={index} sx={{ mt: "1rem" }} />
                                )}
                            </Box>
                        ))}
                    </Box>
                </Box>
            </MainPageContentContainer>
            <OreveFooter>
                <OreveButton
                    value="Sauvegarder"
                    variant="contained"
                    onClick={validation}
                    disabled={
                        showSuccessPopup ||
                        isRuleChanged ||
                        allDaysSelected ||
                        allHoursSelected ||
                        errorDuration ||
                        errorEnergy
                    }
                />
            </OreveFooter>
        </>
    )
}
